/**
 * CatalogSync - Service de synchronisation du catalogue pour le mode hors-ligne
 *
 * Télécharge et met en cache les produits, catégories et vendeurs
 * pour permettre le fonctionnement du POS sans connexion internet.
 */
import { offlineDb, OfflineProduct, OfflineCategory, OfflineVendor } from './OfflineDatabase';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export interface SyncProgress {
  step: 'categories' | 'products' | 'vendors' | 'cleanup';
  progress: number; // 0-100
  current?: number;
  total?: number;
  message?: string;
}

export interface SyncResult {
  success: boolean;
  products: { synced: number; errors: number };
  categories: { synced: number; errors: number };
  vendors: { synced: number; errors: number };
  duration: number;
  error?: string;
}

type ProgressCallback = (progress: SyncProgress) => void;

// ============================================================================
// CLASSE PRINCIPALE
// ============================================================================

export class CatalogSync {
  private tenantId: string;
  private onProgress?: ProgressCallback;
  private abortController: AbortController | null = null;
  // Mode de la dernière synchro produits : 'full' (catalogue entier) ou 'incremental' (delta).
  // Le nettoyage des produits obsolètes ne tourne QU'APRÈS une full (en incrémental, les
  // produits non modifiés gardent un _lastSync ancien et seraient supprimés à tort).
  private lastProductSyncMode: 'full' | 'incremental' = 'full';

  constructor(tenantId: string, onProgress?: ProgressCallback) {
    this.tenantId = tenantId;
    this.onProgress = onProgress;
  }

  /**
   * Annuler la synchronisation en cours
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Synchronisation complète du catalogue
   * À appeler à l'ouverture de caisse ou manuellement
   */
  async syncAll(): Promise<SyncResult> {
    const startTime = Date.now();
    this.abortController = new AbortController();

    const results: SyncResult = {
      success: false,
      products: { synced: 0, errors: 0 },
      categories: { synced: 0, errors: 0 },
      vendors: { synced: 0, errors: 0 },
      duration: 0,
    };

    // Log de début
    await offlineDb.syncLogs.add({
      timestamp: Date.now(),
      type: 'catalog',
      status: 'started',
      details: 'Synchronisation du catalogue démarrée',
    });

    try {
      // 1. Synchroniser les catégories (rapide)
      this.onProgress?.({ step: 'categories', progress: 0, message: 'Chargement des catégories...' });
      const categories = await this.syncCategories();
      results.categories.synced = categories.length;
      logger.log(`[CatalogSync] ${categories.length} catégories synchronisées`);

      // Vérifier si annulé
      if (this.abortController?.signal.aborted) {
        throw new Error('Synchronisation annulée');
      }

      // 2. Synchroniser les produits (plus long, par lots)
      this.onProgress?.({ step: 'products', progress: 0, message: 'Chargement des produits...' });
      const productCount = await this.syncProducts();
      results.products.synced = productCount;
      logger.log(`[CatalogSync] ${productCount} produits synchronisés`);

      // Vérifier si annulé
      if (this.abortController?.signal.aborted) {
        throw new Error('Synchronisation annulée');
      }

      // 3. Synchroniser les vendeurs
      this.onProgress?.({ step: 'vendors', progress: 0, message: 'Chargement des vendeurs...' });
      const vendors = await this.syncVendors();
      results.vendors.synced = vendors.length;
      logger.log(`[CatalogSync] ${vendors.length} vendeurs synchronisés`);

      // 4. Nettoyage des données obsolètes — UNIQUEMENT après une sync full.
      //    En incrémental, les produits non modifiés gardent un _lastSync ancien : le purger
      //    ici viderait le catalogue. Les désactivés sont déjà retirés par le delta ;
      //    la full quotidienne (forcée à 24 h) réconcilie les suppressions définitives.
      this.onProgress?.({ step: 'cleanup', progress: 0, message: 'Nettoyage...' });
      if (this.lastProductSyncMode === 'full') {
        await offlineDb.cleanupStaleProducts(this.tenantId);
      }
      await offlineDb.cleanupOldSyncedSales();

      results.success = true;
      results.duration = Date.now() - startTime;

      // Log de succès
      await offlineDb.syncLogs.add({
        timestamp: Date.now(),
        type: 'catalog',
        status: 'success',
        details: JSON.stringify(results),
        itemCount: results.products.synced + results.categories.synced + results.vendors.synced,
        duration: results.duration,
      });

      logger.log(`[CatalogSync] ✅ Synchronisation terminée en ${results.duration}ms`);

    } catch (error) {
      results.error = error instanceof Error ? error.message : 'Erreur inconnue';

      // Log d'erreur
      await offlineDb.syncLogs.add({
        timestamp: Date.now(),
        type: 'catalog',
        status: 'error',
        details: results.error,
        duration: Date.now() - startTime,
      });

      logger.error(`[CatalogSync] ❌ Erreur:`, error);
    } finally {
      this.abortController = null;
    }

    return results;
  }

  /**
   * Synchroniser les catégories
   */
  private async syncCategories(): Promise<OfflineCategory[]> {
    const response = await api.get('/categories', {
      params: { limit: 500 }, // Toutes les catégories
    });

    const categoriesData = response.data?.data || response.data || [];

    const categories: OfflineCategory[] = categoriesData.map((cat: any) => ({
      id: cat.id,
      tenant_id: this.tenantId,
      name: cat.name,
      parent_id: cat.parent_id || null,
      sort_order: cat.sort_order ?? 0,
      image_url: cat.image_url || null,
      _lastSync: Date.now(),
    }));

    // Upsert en masse
    await offlineDb.categories.bulkPut(categories);

    this.onProgress?.({ step: 'categories', progress: 100 });

    return categories;
  }

  /**
   * Synchroniser les produits par lots
   */
  private async syncProducts(): Promise<number> {
    // --- Choix du mode : incrémental si on a déjà un catalogue + un repère < 24 h ---
    const sinceKey = `catalog_since_${this.tenantId}`;       // repère serveur (ISO) du dernier sync
    const syncedAtKey = `catalog_synced_at_${this.tenantId}`; // horodatage client (ms) du dernier sync
    let storedSince: string | null = null;
    let lastSyncedAtMs = 0;
    try {
      storedSince = localStorage.getItem(sinceKey);
      lastSyncedAtMs = Number(localStorage.getItem(syncedAtKey) || 0);
    } catch { /* localStorage indisponible (mode privé, etc.) → full */ }

    const existingCount = await offlineDb.products
      .where('tenant_id').equals(this.tenantId).count();
    // Full forcée toutes les 24 h : réconcilie prix-seuls (table prices séparée),
    // suppressions définitives, et rafraîchit tous les _lastSync pour le cleanup.
    const ageOk = lastSyncedAtMs > 0 && (Date.now() - lastSyncedAtMs < 24 * 60 * 60 * 1000);
    const incremental = !!storedSince && existingCount > 0 && ageOk;
    this.lastProductSyncMode = incremental ? 'incremental' : 'full';

    let page = 1;
    // 500 dans les DEUX modes. La complète paginait par 100 : 17 requêtes en
    // file indienne pour 1 603 produits, mesuré 2,4 s de réseau contre 1,1 s en
    // pages de 500 — et ces 17 attentes tombaient au premier vendeur du matin,
    // pendant que les premiers clients attendaient (signalé le 10/09/2026).
    // Le poids total est le même (2,9 Mo) : ce qu'on économise, ce sont les
    // allers-retours.
    const limit = 500;
    let totalSynced = 0;
    let hasMore = true;
    let estimatedTotal = 0;
    let serverTime: string | null = null;
    const toDelete: string[] = []; // produits désactivés depuis le dernier sync

    while (hasMore) {
      if (this.abortController?.signal.aborted) {
        throw new Error('Synchronisation annulée');
      }

      const params: any = { page, limit };
      if (incremental) {
        params.updated_since = storedSince; // pas de filtre active → on reçoit aussi les désactivés
      } else {
        params.active = true;
      }

      const response = await api.get('/products', { params });

      const productsData = response.data?.data || response.data || [];
      const meta = response.data?.pagination || response.data?.meta;
      if (response.data?.serverTime) serverTime = response.data.serverTime;

      // Estimer le total pour la barre de progression
      if (meta?.total) {
        estimatedTotal = meta.total;
      } else if (page === 1) {
        estimatedTotal = productsData.length >= limit ? limit * 10 : productsData.length;
      }

      // Upsert des actifs ; en incrémental, on collecte les désactivés pour les retirer
      const offlineProducts: OfflineProduct[] = [];
      for (const p of productsData) {
        if (incremental && p.active === false) {
          toDelete.push(p.id);
          continue;
        }
        offlineProducts.push(this.mapProductToOffline(p));
      }

      if (offlineProducts.length > 0) {
        await offlineDb.products.bulkPut(offlineProducts);
        totalSynced += offlineProducts.length;
      }

      const progress = estimatedTotal > 0
        ? Math.min((totalSynced / estimatedTotal) * 100, 99)
        : (page * 10);

      this.onProgress?.({
        step: 'products',
        progress,
        current: totalSynced,
        total: estimatedTotal,
        message: `${totalSynced} produits ${incremental ? 'mis à jour' : 'chargés'}...`,
      });

      hasMore = productsData.length === limit;
      page++;

      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    // Retirer du cache hors-ligne les produits désactivés depuis le dernier sync
    if (incremental && toDelete.length > 0) {
      await offlineDb.products.bulkDelete(toDelete);
    }

    // Mémoriser le repère serveur pour la prochaine sync incrémentale.
    // Si le serveur ne renvoie pas serverTime (ancienne version API), on NE pose pas
    // le repère → la prochaine sync restera full (dégradation gracieuse, jamais de delta cassé).
    try {
      if (serverTime) localStorage.setItem(sinceKey, serverTime);
      localStorage.setItem(syncedAtKey, String(Date.now()));
    } catch { /* ignore */ }

    this.onProgress?.({ step: 'products', progress: 100, current: totalSynced, total: totalSynced });
    logger.log(`[CatalogSync] produits: mode=${this.lastProductSyncMode}, maj=${totalSynced}, retirés=${toDelete.length}`);

    return totalSynced;
  }

  /**
   * Mapper un produit API vers le format offline
   */
  private mapProductToOffline(apiProduct: any): OfflineProduct {
    // Trouver le prix EUR
    const eurPrice = apiProduct.prices?.find((p: any) => p.currency === 'EUR');
    const priceCents = eurPrice?.amount_cents ?? apiProduct.suggested_price_cents ?? 0;

    return {
      id: apiProduct.id,
      tenant_id: this.tenantId,
      code: apiProduct.code || null,
      barcode: apiProduct.barcode || null,
      name: apiProduct.name,
      description: apiProduct.description || null,
      image_url: apiProduct.image_url || null,
      category_id: apiProduct.category_id || null,
      category_name: apiProduct.category?.name || null,

      price_cents: priceCents,
      currency: 'EUR',

      stock_quantity: apiProduct.stock_quantity ?? 0,
      stock_display: apiProduct.stock_display ?? apiProduct.stock_quantity ?? 0,
      stock_backroom: apiProduct.stock_backroom ?? 0,
      is_weighted: apiProduct.is_weighted ?? false,
      unit: apiProduct.unit || null,

      tax_class: apiProduct.tax_class || 'STANDARD_21',

      is_deposit: apiProduct.is_deposit ?? false,
      deposit_product_id: apiProduct.deposit_product_id || null,
      deposit_qty_per_unit: apiProduct.deposit_qty_per_unit
        ? Number(apiProduct.deposit_qty_per_unit)
        : null,

      lot_promos: Array.isArray(apiProduct.lot_promos) ? apiProduct.lot_promos : undefined,
      lot_normal_expiry_date: apiProduct.lot_normal_expiry_date ?? null,

      version: apiProduct.version ?? 1,
      _lastSync: Date.now(),
    };
  }

  /**
   * Synchroniser les vendeurs
   */
  private async syncVendors(): Promise<OfflineVendor[]> {
    const response = await api.get('/vendors', {
      params: { active: true, limit: 100 },
    });

    const vendorsData = response.data?.data || response.data || [];

    const vendors: OfflineVendor[] = vendorsData.map((v: any) => ({
      id: v.id,
      tenant_id: this.tenantId,
      name: v.name,
      pin_hash: v.pin_hash || '',
      role: v.role || 'CASHIER',
      workstations: v.workstations?.map((w: any) => w.workstation_code) || [],
      active: v.active ?? true,
      _lastSync: Date.now(),
    }));

    // Upsert en masse
    await offlineDb.vendors.bulkPut(vendors);

    this.onProgress?.({ step: 'vendors', progress: 100 });

    return vendors;
  }

  /**
   * Vérifier si une synchronisation est nécessaire
   */
  async needsSync(): Promise<boolean> {
    const lastSync = await offlineDb.syncLogs
      .where('type')
      .equals('catalog')
      .and(log => log.status === 'success')
      .last();

    if (!lastSync) return true;

    // Sync nécessaire si plus de 4 heures
    const fourHoursAgo = Date.now() - 4 * 60 * 60 * 1000;
    return lastSync.timestamp < fourHoursAgo;
  }

  /**
   * Obtenir la dernière date de synchronisation
   */
  async getLastSyncDate(): Promise<Date | null> {
    const lastSync = await offlineDb.syncLogs
      .where('type')
      .equals('catalog')
      .and(log => log.status === 'success')
      .last();

    return lastSync ? new Date(lastSync.timestamp) : null;
  }

  /**
   * Synchroniser un seul produit (mise à jour ponctuelle)
   */
  async syncSingleProduct(productId: string): Promise<OfflineProduct | null> {
    try {
      const response = await api.get(`/products/${productId}`);
      const product = this.mapProductToOffline(response.data);
      await offlineDb.products.put(product);
      return product;
    } catch (error) {
      logger.error(`[CatalogSync] Erreur sync produit ${productId}:`, error);
      return null;
    }
  }

  /**
   * Mettre à jour le stock local d'un produit
   * (utilisé après une vente offline pour garder le stock à jour localement)
   */
  async updateLocalStock(productId: string, quantitySold: number): Promise<void> {
    await offlineDb.products
      .where('id')
      .equals(productId)
      .modify(product => {
        product.stock_quantity = Math.max(0, product.stock_quantity - quantitySold);
        product.stock_display = Math.max(0, product.stock_display - quantitySold);
      });
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Créer une instance de CatalogSync pour un tenant
 */
export function createCatalogSync(tenantId: string, onProgress?: ProgressCallback): CatalogSync {
  return new CatalogSync(tenantId, onProgress);
}
